import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { BASE_URL } from '../constants/api-urls';
import { getCuratorAuthHeader } from '../utils/curatorApiAuth';
import { resolveUserBillingIds, SubscriptionPackage } from '../utils/subscriptionStatus';

type PackageRow = SubscriptionPackage & {
  email?: string;
  subsId?: string;
  custId?: string;
  planId?: string;
};

interface PackageData {
  data: PackageRow[];
}

interface UserInfo {
  email: string;
  subId: string;
  custId: string;
}

interface PackageContextType {
  packageData: PackageData | null;
  isLoading: boolean;
  error: string | null;
  fetchPackageData: (userInfo: UserInfo, force?: boolean) => Promise<PackageData | null>;
  clearCache: () => void;
  refreshCache: () => Promise<void>;
  getCachedData: () => PackageData | null;
}

// Default context value with proper typing
const PackageContext = createContext<PackageContextType>({
  packageData: null,
  isLoading: false,
  error: null,
  fetchPackageData: async () => null,
  clearCache: () => {},
  refreshCache: async () => {},
  getCachedData: () => null,
});

interface PackageContextProviderProps {
  children: React.ReactNode;
}

export const PackageContextProvider: React.FC<PackageContextProviderProps> = ({ children }) => {
  const [packageData, setPackageData] = useState<PackageData | null>(null);
  const [lastFetch, setLastFetch] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep latest values available to stable fetchPackageData without changing identity every render
  const packageDataRef = useRef<PackageData | null>(null);
  const lastFetchRef = useRef<number | null>(null);
  const inflightRef = useRef<Promise<PackageData | null> | null>(null);

  packageDataRef.current = packageData;
  lastFetchRef.current = lastFetch;

  // Cache duration: 10 minutes (600,000ms) - balances performance vs data freshness
  const CACHE_DURATION = 10 * 60 * 1000;

  const fetchPackageData = useCallback(
    async (userInfo: UserInfo, force = false): Promise<PackageData | null> => {
      // Check cache validity - if we have cached data and it's not expired, return it
      if (!force && packageDataRef.current && lastFetchRef.current) {
        const timeDiff = Date.now() - lastFetchRef.current;
        if (timeDiff < CACHE_DURATION) {
          return packageDataRef.current;
        }
      }

      // Share a single in-flight request so concurrent callers never get a premature null
      if (inflightRef.current && !force) {
        return inflightRef.current;
      }

      setIsLoading(true);
      setError(null);

      const request = (async (): Promise<PackageData | null> => {
        try {
          // Use existing /packages endpoint - NO CHANGES to API
          const billing = resolveUserBillingIds(userInfo);
          const response = await fetch(`${BASE_URL}/packages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getCuratorAuthHeader() },
            body: JSON.stringify({
              email: userInfo.email,
              subId: billing.subId,
              custId: billing.custId,
            }),
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await response.json();

          // Cache the response
          setPackageData(data);
          setLastFetch(Date.now());
          packageDataRef.current = data;
          lastFetchRef.current = Date.now();

          return data;
        } catch (err: any) {
          console.error('PackageContext: Package fetch failed:', err);
          setError(err.message || 'Failed to fetch package data');
          // Return existing cached data on error (graceful degradation)
          return packageDataRef.current;
        } finally {
          setIsLoading(false);
          inflightRef.current = null;
        }
      })();

      inflightRef.current = request;
      return request;
    },
    // CACHE_DURATION is constant; refs hold mutable cache state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const clearCache = useCallback(() => {
    setPackageData(null);
    setLastFetch(null);
    setError(null);
    packageDataRef.current = null;
    lastFetchRef.current = null;
    inflightRef.current = null;
  }, []);

  const refreshCache = useCallback(async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      if (userInfo.email) {
        await fetchPackageData(userInfo, true); // Force refresh
      }
    } catch (err) {
      console.error('PackageContext: Failed to refresh cache:', err);
    }
  }, [fetchPackageData]);

  const getCachedData = useCallback(() => packageData, [packageData]);

  const contextValue: PackageContextType = {
    packageData,
    isLoading,
    error,
    fetchPackageData,
    clearCache,
    refreshCache,
    getCachedData,
  };

  return <PackageContext.Provider value={contextValue}>{children}</PackageContext.Provider>;
};

// Custom hook with proper error handling
export const usePackageContext = (): PackageContextType => {
  const context = useContext(PackageContext);
  if (!context) {
    throw new Error('usePackageContext must be used within a PackageContextProvider');
  }
  return context;
};
